import * as yup from "yup";

/**
 * Password validation constants and schemas for consistent use across the application
 */

// Password complexity requirements
const passwordMinLength = 12;
const passwordComplexityTest = new RegExp(
  String.raw`^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^\w\s]).{${passwordMinLength},}$`
);
export let passwordRequirements;
passwordRequirements = {
  minLength: passwordMinLength,
  regex: passwordComplexityTest,
};

// German helper text for password requirements
export const passwordMessages = {
  required: "Passwort ist erforderlich.",
  complexity: `Das Passwort muss mindestens ${passwordMinLength} Zeichen lang sein und Groß- und Kleinbuchstaben, Zahlen und Sonderzeichen enthalten.`,
  confirmationRequired: "Bitte bestätigen Sie das Passwort.",
  confirmationMatch: "Die Passwörter müssen übereinstimmen.",
  currentRequired: "Das aktuelle Passwort ist erforderlich.",
  newRequired: "Ein neues Passwort ist erforderlich.",
  newConfirmationRequired: "Bitte bestätigen Sie das neue Passwort.",
};

/**
 * Base password validation schema with complexity requirements
 */
export const requiredPasswordSchema = yup
  .string()
  .required(passwordMessages.required)
  .matches(passwordRequirements.regex, passwordMessages.complexity);

/**
 * Optional password schema (for edit forms where password change is optional)
 */
export const optionalPasswordSchema = yup
  .string()
  .test(
    "is-strong-or-empty",
    passwordMessages.complexity,
    (value) =>
      !value || value.length === 0 || passwordRequirements.regex.test(value)
  );

/**
 * Password confirmation schema that depends on a password field
 * @param {string} passwordFieldName - Name of the password field to match against
 * @param {boolean} isRequired - Whether confirmation is required
 */
export const passwordConfirmationSchema = (
  passwordFieldName = "password",
  isRequired = true
) => {
  let schema = yup
    .string()
    .oneOf([yup.ref(passwordFieldName)], passwordMessages.confirmationMatch);

  if (isRequired) {
    schema = schema.required(passwordMessages.confirmationRequired);
  }

  return schema;
};

/**
 * Conditional password confirmation schema for optional password changes
 * @param {string} passwordFieldName - Name of the password field to match against
 */
export const conditionalPasswordConfirmationSchema = (
  passwordFieldName = "password"
) => {
  return yup.string().when(passwordFieldName, {
    is: (password) => password && password.length > 0,
    then: () =>
      yup
        .string()
        .oneOf([yup.ref(passwordFieldName)], passwordMessages.confirmationMatch)
        .required(passwordMessages.confirmationRequired),
    otherwise: () => yup.string(),
  });
};

/**
 * Complete validation schema for password change forms (current + new + confirmation)
 */
export const passwordChangeSchema = {
  current_password: yup.string().required(passwordMessages.currentRequired),
  new_password: yup
    .string()
    .required(passwordMessages.newRequired)
    .matches(passwordRequirements.regex, passwordMessages.complexity),
  new_password_confirmation: yup
    .string()
    .oneOf([yup.ref("new_password")], passwordMessages.confirmationMatch)
    .required(passwordMessages.newConfirmationRequired),
};

/**
 * Helper text for password fields that can be used in form components
 */
export const passwordHelperText = {
  requirements: `Mindestens ${passwordMinLength} Zeichen mit Groß- und Kleinbuchstaben, Zahlen und Sonderzeichen`,
  optional: "Leer lassen, um das Passwort unverändert zu lassen",
};
